import { Box, Toolbar } from '@material-ui/core';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useForm } from 'react-hook-form';
import { LatLng } from 'leaflet';
import { Dispatch } from 'redux';

import { AttachmentType } from '../../../models';
import {
	Attachment,
	AttachmentEditorStates,
	AppState,
	attachmentEditorCloseDialog,
	ILatLng,
	pushMessage
} from '../../../redux';
import {
	AttachmentEditorOverview,
	DialogWindow,
	FullscreenDialogFooter,
	FullscreenDialogHeader,
	ProgressStepper
} from '../../molecules';
import { AttachmentDataForm, AttachmentLocationSelector } from '..';

import './map-attachment-edit-dialog.styles.scss';
import { usePrevious } from '../../../hooks';
import { useTranslation } from 'react-i18next';

export interface IMapAttachmentEditDialogOwnProps {
	attachmentCount: number;
	mapId: string;
	casperId: string;
	projection?: string;
	corner: LatLng;
	oppositeCorner: LatLng;
	addAttachment: (attachment: Attachment, file?: File) => void;
	editAttachment: (attachment: Attachment, file?: File) => void;
}

export interface IMapAttachmentMapParams {
	zoom: number;
	center: LatLng;
}

export interface IMapAttachmentEditDialogStateProps {
	isOpen: boolean;
	isCreating: boolean;
	attachment?: FormAttachment;
	closeDialog: () => void;
	pushMessage: (message: string) => void;
}

export type FormAttachment = {
	id: string | null;
	title: string;
	description: string;
	location: string;
	pointOnMap: LatLng | null;
	type: AttachmentType | null;
	order: number;
};

function MapAttachmentEditDialogView(
	props: IMapAttachmentEditDialogOwnProps & IMapAttachmentEditDialogStateProps
): ReactElement {
	const {
		mapId,
		casperId,
		projection,
		corner,
		oppositeCorner,
		isOpen,
		isCreating,
		attachment,
		attachmentCount,
		closeDialog,
		addAttachment,
		editAttachment,
		pushMessage
	} = props;

	const { t } = useTranslation(['map', 'general']);

	const [editorState, setEditorState] = useState<AttachmentEditorStates>(
		isCreating
			? AttachmentEditorStates.DataForm
			: AttachmentEditorStates.Overview
	);

	const { control, watch, getValues, reset, formState, setValue } =
		useForm<FormAttachment>({ mode: 'onChange' });
	const previousIsOpen: boolean | undefined = usePrevious<boolean>(isOpen);
	const [mapParams, setMapParams] =
		useState<IMapAttachmentMapParams | undefined>();
	const [file, setFile] = useState<File>();
	const [fileTooBig, setFileTooBig] = useState<boolean>(false);

	const activeStep: number = useMemo<number>(() => {
		switch (editorState) {
			case AttachmentEditorStates.DataForm:
				return 0;
			case AttachmentEditorStates.SetLocation:
				return 1;
			case AttachmentEditorStates.Overview:
				return 2;
			default:
				return 0;
		}
	}, [editorState]);

	const pinPosition: LatLng | null = watch().pointOnMap;

	useEffect(() => {
		if (!previousIsOpen && isOpen) {
			setEditorState(
				isCreating
					? AttachmentEditorStates.DataForm
					: AttachmentEditorStates.Overview
			);
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && attachment) {
			reset(attachment);
		}
	}, [attachment, isOpen]);

	function setFormValidState(isValid: boolean): void {
		setFileTooBig(!isValid);
	}

	function changeType(type: AttachmentType): void {
		reset({ ...getValues(), location: '', type });
	}

	function confirmStep(): void {
		switch (activeStep) {
			case 0: {
				setEditorState(AttachmentEditorStates.SetLocation);
				break;
			}
			case 1: {
				setEditorState(AttachmentEditorStates.Overview);
				break;
			}
			case 2: {
				saveAttachment();
				break;
			}
		}
	}

	function goBack(): void {
		switch (activeStep) {
			case 1:
				setEditorState(AttachmentEditorStates.DataForm);
				break;
			case 2:
				setEditorState(AttachmentEditorStates.SetLocation);
				break;
			default:
				return;
		}
	}

	function changePinPosition(position?: LatLng): void {
		reset({ ...getValues(), pointOnMap: position ? position : null });
	}

	function clearAndCloseDialog(): void {
		reset({
			title: '',
			description: '',
			order: attachmentCount + 2,
			location: '',
			type: null,
			pointOnMap: null
		});
		setEditorState(AttachmentEditorStates.DataForm);
		setFile(undefined);
		closeDialog();
	}

	function saveAttachment(): void {
		const formValue: FormAttachment = getValues();

		if (formValue.type === null) return;

		const pointOnMap: ILatLng | undefined =
			formValue.pointOnMap !== null
				? {
						lat: formValue.pointOnMap.lat,
						lng: formValue.pointOnMap.lng
				  }
				: undefined;

		const attachment: Attachment = {
			title: formValue.title,
			description: formValue.description,
			order: formValue.order,
			location:
				formValue.location && formValue.location.length > 0
					? formValue.location
					: undefined,
			type: formValue.type,
			isAuthorContent: false,
			pointOnMap
		};

		if (isCreating) {
			addAttachment(attachment, file);
			pushMessage(t('map:attachments.notification.addAttachment'));
		} else {
			editAttachment(attachment, file);
			pushMessage(t('map:attachments.notification.editAttachment'));
		}
		clearAndCloseDialog();
	}

	return (
		<DialogWindow
			fullScreen
			className="map-attachment-edit-dialog"
			open={isOpen}
		>
			<FullscreenDialogHeader
				onClose={clearAndCloseDialog}
				title={
					isCreating
						? t('map:attachments.addingAttachment')
						: t('map:attachments.editAttachment')
				}
			/>
			<ProgressStepper
				activeStep={activeStep}
				labels={[
					t('map:attachments.stepper.chooseAttachment'),
					t('map:attachments.stepper.addLocation'),
					t('map:attachments.stepper.finish')
				]}
			/>
			<Box className="map-attachment-edit-dialog-content">
				{editorState === AttachmentEditorStates.DataForm && (
					<AttachmentDataForm
						control={control}
						watch={watch}
						changeType={changeType}
						isCreating={isCreating}
						valid={formState.isValid}
						onSelectFile={setFile}
						setValid={setFormValidState}
						setValue={setValue}
						getValues={getValues}
					/>
				)}
				{editorState === AttachmentEditorStates.SetLocation && (
					<AttachmentLocationSelector
						mapId={mapId}
						casperId={casperId}
						corner={corner}
						oppositeCorner={oppositeCorner}
						position={pinPosition}
						onPositionChange={changePinPosition}
						projection={projection}
						mapParams={mapParams}
						setMapParams={setMapParams}
					/>
				)}
				{editorState === AttachmentEditorStates.Overview && (
					<AttachmentEditorOverview
						mapId={mapId}
						casperId={casperId}
						corner={corner}
						oppositeCorner={oppositeCorner}
						projection={projection}
						attachment={getValues()}
						file={file}
						editData={() =>
							setEditorState(AttachmentEditorStates.DataForm)
						}
						editLocation={() =>
							setEditorState(AttachmentEditorStates.SetLocation)
						}
						mapParams={mapParams}
						setMapParams={setMapParams}
					/>
				)}
			</Box>
			<Toolbar className="map-attachment-toolbar" />
			<FullscreenDialogFooter
				activeStep={activeStep}
				confirmText={
					activeStep === 2
						? isCreating
							? t('map:attachments.addAttachment')
							: t('map:attachments.editAttachment')
						: t('general:nextStep')
				}
				onConfirmStep={confirmStep}
				onBackClicked={goBack}
				confirmDisabled={
					!formState.isValid || (fileTooBig && formState.isValid)
				}
			/>
		</DialogWindow>
	);
}

function mapStateToProps(state: AppState) {
	return {
		isOpen: state.attachmentsEditor.dialogOpen,
		isCreating: state.attachmentsEditor.isCreating,
		attachment: state.attachmentsEditor.currentlyEditing
	};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		closeDialog: () => dispatch(attachmentEditorCloseDialog()),
		pushMessage: (message: string) => dispatch(pushMessage(message))
	};
}

export const MapAttachmentEditDialog = connect(
	mapStateToProps,
	mapDispatchToProps
)(MapAttachmentEditDialogView);
